import type { UI } from '@/v2/types';
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
  Box,
  Flex,
  Icon,
  Image,
  NakedButton,
  Text,
} from '@qga/roo-ui/components';

// shared
import CollapsiblePackageSection, {
  Heading,
} from '@shared/v2/components/CollapsiblePackageSection';
import { BEST_RATE_ID, BEST_RATE_TEXT } from '@shared/v2/constants/packages';
import useSearchQuery from '@shared/v2/hooks/useSearchQuery';
import { PackageOption } from '@shared/v2/utils/getPackageOptions';

import Button from '@/components/Button';
// v2
import { sale } from '@/v2/components/icons';
import { useSectionControls } from '@/v2/components/pages/PackageView/contexts/SectionControlsContext';
import { openPackageOptionPanelDataLayerEvent } from '@/v2/components/pages/PackageView/utils/dataLayer/dataLayer';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import { usePackagePageV3 } from '@/v2/hooks/featureFlags/packagePageV3/usePackagePageV3';
import dataLayer from '@/v2/utils/dataLayer';
import PackageDetails from '../../../../ui/PackageDetails';
import { PackageOptionContent } from './ui';

export type PackageOptionContainerProps = {
  packageOptions: PackageOption[] | undefined;
  selectedPackageOption: PackageOption | undefined;
  procuredOffer: UI.ProcuredOffer | null;
  propertyContent: UI.Property;
};

const StyledButton = styled(Button)`
  height: 48px;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 1.5px;
`;

const getCtaText = (isPanelOpen: boolean, isPackagePageV3Enabled = false) => {
  if (isPackagePageV3Enabled && isPanelOpen) {
    return 'Done';
  }
  return isPanelOpen ? 'Cancel' : 'Change package option';
};

const StyledSection = styled(CollapsiblePackageSection)`
  overflow-x: hidden;
`;

const PackageOptionContainer = ({
  packageOptions,
  selectedPackageOption,
  propertyContent,
  procuredOffer,
}: PackageOptionContainerProps) => {
  const shouldUseNewEvents = useGA4Events();
  const [isPageInitialized, setIsPageInitialized] = useState(false);
  const isPackagePageV3Enabled = usePackagePageV3();

  // Package option state
  const { searchQuery, setSearchQuery, isReady } = useSearchQuery();
  const { packageOption, originCode } = searchQuery || {};

  const selectedPackageTitle =
    packageOption === BEST_RATE_ID
      ? BEST_RATE_TEXT
      : selectedPackageOption?.title;

  // Collapse controls
  const {
    packageOptions: { open, isOpen, close, toggle },
  } = useSectionControls();

  useEffect(() => {
    // Package options should be expanded on load when V3 Feature flag is enabled.
    // Wait for the feature flag to load and open
    if (isPageInitialized || !isReady) {
      return;
    }

    // Open the options if no packageOption
    if (!packageOption && !isOpen && !selectedPackageOption) {
      open();
    } else if (isPackagePageV3Enabled && !isOpen) {
      open();
    }
    setIsPageInitialized(true);
  }, [
    isOpen,
    isPackagePageV3Enabled,
    isPageInitialized,
    isReady,
    open,
    packageOption,
    selectedPackageOption,
  ]);

  const handlePackageOptionChange = (packageOption: string) => {
    if (shouldUseNewEvents) {
      const option = packageOptions?.find((o) => o.id === packageOption);
      dataLayer.tileClickEvent({
        groupName: 'Package options',
        itemName: option?.title || 'Best Available Rates',
        itemText: 'SELECT PACKAGE',
        index: option?.optionNumber || 1,
      });
      dataLayer.ctaClickEvent({
        itemText: 'Select package',
      });
    }
    setSearchQuery({ packageOption });
    close();
  };

  const togglePanel = () => {
    if (!isOpen) {
      openPackageOptionPanelDataLayerEvent();
      if (shouldUseNewEvents) {
        dataLayer.ctaClickEvent({
          itemType: 'button',
          itemText: 'Change package option',
        });
      }
    }
    toggle();
  };

  return (
    <Box data-testid="PACKAGE_OPTION_CONTAINER">
      <StyledSection
        icon={<Image src={sale} alt="Sale icon" width="48px" />}
        headerContent={
          isOpen ? (
            <>
              <Heading isBig>Package options</Heading>
              <Heading>Select your package option</Heading>
            </>
          ) : (
            <Flex flexDirection="column" alignItems="flex-start">
              <Heading>Package options</Heading>
              <Heading isBig>{selectedPackageTitle ?? ''}</Heading>
              {selectedPackageOption && (
                <PackageDetails
                  propertyContent={propertyContent}
                  procuredOffer={procuredOffer}
                  inclusions={
                    selectedPackageOption.cheapestOffer.leadInOffer.inclusions
                  }
                />
              )}
            </Flex>
          )
        }
        sectionContent={
          <PackageOptionContent
            packageOptions={packageOptions}
            selectedInclusionId={packageOption}
            onSelectPackageOption={handlePackageOptionChange}
            propertyContent={propertyContent}
            procuredOffer={procuredOffer}
            disabled={!originCode}
          />
        }
        collapseButton={
          isOpen && !isPackagePageV3Enabled ? (
            <Box textAlign={'center'}>
              <NakedButton
                onClick={togglePanel}
                data-testid="PACKAGE_OPTION_CANCEL_CTA"
                color="brand.primary"
              >
                <Flex gap="6px">
                  <Text
                    fontSize="18px"
                    lineHeight="27px"
                    fontWeight="600"
                    style={{ textTransform: 'uppercase' }}
                  >
                    {getCtaText(isOpen)}
                  </Text>
                  <Icon color={'brand.primary'} name="close" mt="2px" />
                </Flex>
              </NakedButton>
            </Box>
          ) : (
            <StyledButton
              data-testid="PACKAGE_OPTION_CONTAINER_CTA"
              variant="primary"
              width={['100%', null, '263px']}
              outline
              onClick={togglePanel}
            >
              {getCtaText(isOpen, isPackagePageV3Enabled)}
            </StyledButton>
          )
        }
        isOpen={isOpen}
        showButton={!isOpen || (isOpen && !!packageOption)}
      />
    </Box>
  );
};

export default PackageOptionContainer;
